import {
    ChatBubbleOvalLeftIcon,
    DocumentDuplicateIcon,
    IdentificationIcon
} from '@heroicons/react/24/outline';

export type ApproveTab = 'case' | 'chat' | 'files';

interface TabSwitcherProps {
    active: ApproveTab;
    onChange: (tab: ApproveTab) => void;
    /** When true, a small red dot is shown on the Chat tab to flag unread. */
    chatUnread: boolean;
}

/**
 * Pill switcher between the Case (patient + form), Chat, and Files tabs in
 * the right sidebar of the Approve page. The selected tab fills with the
 * dark ink color; the unread dot on the Chat tab clears when the user
 * switches to it.
 */
export function TabSwitcher({ active, onChange, chatUnread }: TabSwitcherProps) {
    return (
        <div
            role="tablist"
            aria-label="Approve case sections"
            className="flex-shrink-0 flex gap-1 mb-3 bg-white border border-gray-200 rounded-xl p-1"
        >
            <TabButton
                id="case"
                label="Case"
                active={active === 'case'}
                onClick={() => onChange('case')}
                icon={<IdentificationIcon className="w-4 h-4" />}
            />
            <TabButton
                id="chat"
                label="Message ODO"
                active={active === 'chat'}
                unread={chatUnread}
                onClick={() => onChange('chat')}
                icon={<ChatBubbleOvalLeftIcon className="w-4 h-4" />}
            />
            <TabButton
                id="files"
                label="Files"
                active={active === 'files'}
                onClick={() => onChange('files')}
                icon={<DocumentDuplicateIcon className="w-4 h-4" />}
            />
        </div>
    );
}

interface TabButtonProps {
    id: ApproveTab;
    label: string;
    active: boolean;
    onClick: () => void;
    unread?: boolean;
    icon?: React.ReactNode;
}

function TabButton({ id, label, active, onClick, unread = false, icon }: TabButtonProps) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`approve-tab-${id}`}
            onClick={onClick}
            className={`relative flex-1 h-10 rounded-lg flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors ${
                active
                    ? 'bg-gray-900 text-white'
                    : 'bg-transparent text-gray-600 hover:text-gray-900'
            }`}
        >
            {icon}
            {label}
            {unread && (
                <span
                    aria-label="Unread messages"
                    className="absolute top-1.5 right-2.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"
                />
            )}
        </button>
    );
}
